import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Loader2, Rocket, Building2, ArrowRight } from "lucide-react";

interface DealTypeCard {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  status: string;
  step_count: number;
}

const DealsV2Home = () => {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspace();
  const [dealTypes, setDealTypes] = useState<DealTypeCard[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [selectedDealType, setSelectedDealType] = useState<DealTypeCard | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dealName, setDealName] = useState("");
  const [dealNameError, setDealNameError] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) { navigate("/auth"); return; }

      const { data: types } = await supabase
        .from("deal_types")
        .select("*")
        .eq("status", "active")
        .order("name");

      if (!types) { setLoading(false); return; }

      const { data: steps } = await supabase
        .from("deal_type_steps")
        .select("deal_type_id");

      const stepCounts: Record<string, number> = {};
      steps?.forEach((s: any) => { stepCounts[s.deal_type_id] = (stepCounts[s.deal_type_id] || 0) + 1; });

      setDealTypes(types.map((t: any) => ({
        ...t,
        step_count: stepCounts[t.id] || 0,
      })));
      setLoading(false);
    };
    fetch();
  }, [navigate]);

  const handleCardClick = (dt: DealTypeCard) => {
    setSelectedDealType(dt);
    setDealName("");
    setDealNameError("");
    setDialogOpen(true);
  };

  const handleStartDeal = async () => {
    if (!dealName.trim()) {
      setDealNameError("Please enter a deal name");
      return;
    }
    if (!selectedDealType) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !activeWorkspaceId) {
      toast({ title: "Error", description: "You must be logged in with an active workspace", variant: "destructive" });
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase
        .from("deals")
        .insert({
          user_id: session.user.id,
          workspace_id: activeWorkspaceId,
          deal_type_id: selectedDealType.id,
          name: dealName.trim(),
          status: "draft",
          strategy: "value_add",
        })
        .select("id")
        .single();

      if (error) throw error;
      setDialogOpen(false);
      navigate(`/deals-v2/${data.id}`);
    } catch (err: any) {
      toast({ title: "Error", description: "Failed to create deal: " + err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <div className="px-4 sm:px-6 py-8 sm:py-12 max-w-4xl mx-auto space-y-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-5 h-5 text-accent" />
            <span className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">Deals V2</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Start a New Deal</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Select a deal type to begin your underwriting analysis.
          </p>
        </div>

        {dealTypes.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No active deal types available yet.</p>
            <p className="text-xs mt-1">Contact your admin to configure deal types.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dealTypes.map((dt) => (
              <Card
                key={dt.id}
                className="p-6 cursor-pointer hover:border-accent/40 hover:shadow-md transition-all group"
                onClick={() => handleCardClick(dt)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-accent" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                </div>
                <h3 className="font-display text-base font-semibold mb-1">{dt.name}</h3>
                {dt.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{dt.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-2xs">{dt.step_count} steps</Badge>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Deal Name Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Name Your Deal</DialogTitle>
            <DialogDescription>
              {selectedDealType?.name} — Enter a name to identify this deal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="deal-name">Deal Name</Label>
            <Input
              id="deal-name"
              placeholder="e.g. 123 Main St Acquisition"
              value={dealName}
              onChange={(e) => { setDealName(e.target.value); setDealNameError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleStartDeal(); }}
              autoFocus
              className={dealNameError ? "border-destructive" : ""}
            />
            {dealNameError && <p className="text-xs text-destructive">{dealNameError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleStartDeal} disabled={creating}>
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              Start Deal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealsV2Home;
